import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ApiError,
  ConnectedFacility,
  ConnectedFacilityService,
  ProviderRejectionReasonEnum,
} from '../../../api';
import { generateZEnumFromEnum } from '../../../utils/forms';
import { connectionRequestsQueryKeys } from '../../query-keys-factories/connection-requests';
import { userQueryKeys } from '../../query-keys-factories/user';
import { handleApiError } from '../../utils/errorhandling';
import { FormSelectField } from '../FormSelectField';

const validationSchema = z.object({
  rejection_reason: generateZEnumFromEnum(window.REJECTION_REASONS),
  rejection_details: z.string().optional(),
});

export const RejectConnectionDialog = ({
  connectionRequest,
  show,
  closeDialog,
  connectionId,
  onRejectSuccess,
}: {
  connectionRequest: ConnectedFacility;
  show: boolean;
  closeDialog: () => void;
  connectionId?: string;
  onRejectSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ApiError | undefined>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    rejection_reason?: boolean;
    rejection_details?: boolean;
  }>({});

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const reason = useWatch({ control, name: 'rejection_reason' });

  const mutationRejectConnection = useMutation({
    mutationFn: async ({
      id,
      rejectionData,
    }: {
      id: string;
      rejectionData: {
        provider_rejection_reason?: ProviderRejectionReasonEnum;
        provider_rejection_details?: string;
      };
    }) => {
      await ConnectedFacilityService.connectedFacilityRejectPartialUpdate({
        id,
        requestBody: rejectionData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      if (connectionId) {
        queryClient.invalidateQueries(['connected-facility', connectionId]);
      }
      closeDialog();
      onRejectSuccess?.();
    },
  });

  const onSubmit = async (formData) => {
    try {
      await mutationRejectConnection.mutateAsync({
        id: connectionRequest.id,
        rejectionData: {
          provider_rejection_reason: formData.rejection_reason,
          provider_rejection_details: formData.rejection_details || '',
        },
      });
      toast.success('Connection request rejected successfully.');
      closeDialog();
    } catch (error) {
      handleApiError(error, {
        messagePrefix: 'Error rejecting connection request',
      });
    }
  };

  const handleSubmitClick = () => {
    clearErrors();
    setShowServerFieldErrors({});
  };

  const handleClose = () => {
    const defaultReason = Object.keys(window.REJECTION_REASONS)[0];

    reset({
      rejection_reason: defaultReason,
      details: '',
    });
    closeDialog();
  };

  return (
    <Modal show={show} onHide={closeDialog}>
      <Modal.Header closeButton>
        <Modal.Title>Reject Connection Request</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body style={{ maxHeight: '60vh' }}>
          <FormSelectField
            name="rejection_reason"
            label="Please provide a reason for the denial:"
            options={window.REJECTION_REASONS}
            serverErrors={serverErrors}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
          />
          {reason === ProviderRejectionReasonEnum.OTHER && (
            <Form.Group>
              <Form.Label>Provide Rejection Reason</Form.Label>
              <Form.Control type="text" as="textarea" {...register('rejection_details')} />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" onClick={() => handleSubmitClick()}>
            Reject
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
